/**
 * Child Node Component
 *
 * Compact node for display inside group nodes.
 * Simpler than the main nodes, optimized for nested display.
 */

import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import {
  WorkspaceNodeData,
  getNodeTypeIcon,
  getNodeTypeColor,
  getNodeTypeLabel,
} from '../types';

// Extended data type for child nodes
export interface ChildNodeData extends WorkspaceNodeData {
  onFindingClick?: (findingId: string) => void;
}

function ChildNode({ id, data, selected }: NodeProps<ChildNodeData>) {
  const {
    nodeType,
    label,
    relevanceNote,
    content,
    findingIds,
    onFindingClick,
  } = data;

  const color = getNodeTypeColor(nodeType);
  const icon = getNodeTypeIcon(nodeType);
  const typeLabel = getNodeTypeLabel(nodeType);

  const hasFindingIds = Array.isArray(findingIds) && findingIds.length > 0;

  const handleFindingClick = (e: React.MouseEvent, findingId: string) => {
    e.stopPropagation();
    onFindingClick?.(findingId);
  };

  return (
    <div
      className={`
        bg-surface rounded-md border-2 shadow-sm min-w-[160px] max-w-[220px]
        transition-all duration-200
        ${selected ? 'ring-2 ring-accent ring-offset-1' : ''}
      `}
      style={{ borderColor: color }}
    >
      {/* Input Handle */}
      <Handle
        type="target"
        position={Position.Top}
        className="w-2 h-2 !bg-fg-tertiary border border-white"
      />

      {/* Header */}
      <div
        className="px-2 py-1.5 rounded-t-sm flex items-center gap-1.5"
        style={{ backgroundColor: `${color}10` }}
      >
        {/* Type Badge */}
        <div
          className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold"
          style={{ backgroundColor: color }}
        >
          {icon}
        </div>

        <span className="text-[10px] font-medium uppercase tracking-wide" style={{ color }}>
          {typeLabel}
        </span>
      </div>

      {/* Content */}
      <div className="px-2 py-1.5">
        <h4 className="text-xs font-semibold text-fg mb-0.5 line-clamp-2">
          {label}
        </h4>
        {(content || relevanceNote) && (
          <p className="text-[10px] text-fg-tertiary line-clamp-2">
            {content || relevanceNote}
          </p>
        )}

        {/* Finding links */}
        {hasFindingIds && (
          <div className="mt-1.5 pt-1.5 border-t border-divider">
            <div className="flex flex-wrap gap-1">
              {findingIds!.slice(0, 3).map((findingId) => (
                <button
                  key={findingId}
                  onClick={(e) => handleFindingClick(e, findingId)}
                  className="px-1.5 py-0.5 text-[9px] bg-success-bg text-success rounded hover:bg-success-bg transition-colors font-medium"
                >
                  {findingId}
                </button>
              ))}
              {findingIds!.length > 3 && (
                <span className="px-1.5 py-0.5 text-[9px] text-fg-tertiary">
                  +{findingIds!.length - 3}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="w-2 h-2 !bg-fg-tertiary border border-white"
      />
    </div>
  );
}

export default memo(ChildNode);
